var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Copyright 2022 The Parca Authors
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
import { useMemo } from 'react';
import { Select } from '@parca/components';
export var wellKnownProfiles = {
    'block:contentions:count:contentions:count': {
        name: 'Block Contentions Total',
        help: 'Stack traces that led to blocking on synchronization primitives.',
    },
    'block:delay:nanoseconds:contentions:count': {
        name: 'Block Contention Time Total',
        help: 'Time delayed stack traces caused by blocking on synchronization primitives.',
    },
    // Unfortunately, fgprof does not set the period type and unit.
    'fgprof:samples:count::': {
        name: 'Fgprof Samples Total',
        help: 'CPU profile samples observed regardless of their current On/Off CPU scheduling status',
    },
    // Unfortunately, fgprof does not set the period type and unit.
    'fgprof:time:nanoseconds::': {
        name: 'Fgprof Samples Time Total',
        help: 'CPU profile measured regardless of their current On/Off CPU scheduling status in nanoseconds',
    },
    'goroutine:goroutine:count:goroutine:count': {
        name: 'Goroutine Created Total',
        help: 'Stack traces that created all current goroutines.',
    },
    'memory:alloc_objects:count:space:bytes': {
        name: 'Memory Allocated Objects Total',
        help: 'A sampling of all past memory allocations by objects.',
    },
    'memory:alloc_space:bytes:space:bytes': {
        name: 'Memory Allocated Bytes Total',
        help: 'A sampling of all past memory allocations in bytes.',
    },
    'memory:alloc_objects:count:space:bytes:delta': {
        name: 'Memory Allocated Objects Delta',
        help: 'A sampling of all memory allocations during the observation by objects.',
    },
    'memory:alloc_space:bytes:space:bytes:delta': {
        name: 'Memory Allocated Bytes Delta',
        help: 'A sampling of all memory allocations during the observation in bytes.',
    },
    'memory:inuse_objects:count:space:bytes': {
        name: 'Memory In-Use Objects',
        help: 'A sampling of memory allocations of live objects by objects.',
    },
    'memory:inuse_space:bytes:space:bytes': {
        name: 'Memory In-Use Bytes',
        help: 'A sampling of memory allocations of live objects by bytes.',
    },
    'mutex:contentions:count:contentions:count': {
        name: 'Mutex Contentions Total',
        help: 'Stack traces of holders of contended mutexes.',
    },
    'mutex:delay:nanoseconds:contentions:count': {
        name: 'Mutex Contention Time Total',
        help: 'Time delayed stack traces caused by contended mutexes.',
    },
    'process_cpu:cpu:nanoseconds:cpu:nanoseconds:delta': {
        name: 'Process CPU Nanoseconds',
        help: 'CPU profile measured by the process itself in nanoseconds.',
    },
    'process_cpu:samples:count:cpu:nanoseconds:delta': {
        name: 'Process CPU Samples',
        help: 'CPU profile samples observed by the process itself.',
    },
    'parca_agent_cpu:samples:count:cpu:nanoseconds:delta': {
        name: 'CPU Samples',
        help: 'CPU profile samples observed by Parca Agent.',
    },
};
function flexibleWellKnownProfileMatching(name) {
    var prefixExcludedName = name.split(':').slice(1).join(':');
    var deltaExcludedName = prefixExcludedName.replace(/:delta$/, '');
    var requiredKey = Object.keys(wellKnownProfiles).find(function (key) {
        if (key.includes(deltaExcludedName)) {
            return true;
        }
        return false;
    });
    return requiredKey != null ? wellKnownProfiles[requiredKey] : undefined;
}
export function profileSelectElement(name, flexibleKnownProfilesDetection) {
    var wellKnown = !flexibleKnownProfilesDetection
        ? wellKnownProfiles[name]
        : flexibleWellKnownProfileMatching(name);
    if (wellKnown === undefined)
        return { active: _jsx(_Fragment, { children: name }), expanded: _jsx(_Fragment, { children: name }) };
    var title = wellKnown.name.replace(/ /g, '\u00a0');
    return {
        active: _jsx(_Fragment, { children: title }),
        expanded: (_jsxs(_Fragment, { children: [_jsx("span", { children: title }), _jsx("br", {}), _jsx("span", __assign({ className: "text-xs" }, { children: wellKnown.help }))] })),
    };
}
export var normalizeProfileTypesData = function (types) {
    return types
        .map(function (type) {
        return "".concat(type.name, ":").concat(type.sampleType, ":").concat(type.sampleUnit, ":").concat(type.periodType, ":").concat(type.periodUnit).concat(type.delta ? ':delta' : '');
    })
        .sort(function (a, b) {
        return a.localeCompare(b);
    });
};
var ProfileTypeSelector = function (_a) {
    var profileTypesData = _a.profileTypesData, _b = _a.loading, loading = _b === void 0 ? false : _b, error = _a.error, selectedKey = _a.selectedKey, onSelection = _a.onSelection, _c = _a.flexibleKnownProfilesDetection, flexibleKnownProfilesDetection = _c === void 0 ? false : _c;
    var profileNames = useMemo(function () {
        return (error === undefined || error == null) &&
            profileTypesData !== undefined &&
            profileTypesData != null
            ? normalizeProfileTypesData(profileTypesData.types)
            : [];
    }, [profileTypesData, error]);
    var profileLabels = profileNames.map(function (name) { return ({
        key: name,
        element: profileSelectElement(name, flexibleKnownProfilesDetection),
    }); });
    return (_jsx(Select, { items: profileLabels, selectedKey: selectedKey, onSelection: onSelection, placeholder: "Select profile...", loading: loading }));
};
export default ProfileTypeSelector;
